import React from "react";
import QRCode from "react-qr-code";
import { useAuth } from "../../context/AuthContext";

const ReceiptTailor = React.forwardRef(({ assignment }, ref) => {
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  if (!assignment) return null;

  const { tailor, orderItem, laborCost, assignedAt, notes } = assignment;
  const order = orderItem?.order;
  const customer = order?.customer;
  const inventory = orderItem?.inventory;
  const lot = inventory?.lot;

  // Generate QR code data - contains URL to open assignment details
  const qrData = `${window.location.origin}/assignments/${assignment.id}`;

  // Format measurements for display
  const formatMeasurements = (measurements) => {
    if (!measurements) return null;

    // Handle different measurement formats
    if (typeof measurements === 'string') {
      return measurements;
    }

    // If it's an object with measurement types
    if (typeof measurements === 'object') {
      return Object.entries(measurements).map(([type, values]) => {
        if (typeof values === 'object') {
          const measurementsList = Object.entries(values)
            .map(([key, val]) => `${key}: ${val}`)
            .join(', ');
          return `${type}: ${measurementsList}`;
        }
        return `${type}: ${values}`;
      }).join(' | ');
    }
    return null;
  };

  return (
    <div
      ref={ref}
      style={{
        width: "340px",
        border: "1px solid #000",
        fontFamily: "Arial",
        fontSize: "12px",
        margin: "auto",
        background: "#fff"
      }}
    >
      {/* Header */}
      <div style={{ textAlign: "center", padding: "5px", borderBottom: "2px solid #1f6b52" }}>
        <h3 style={{ margin: 0, color: "#1f6b52" }}>RCITMS TAILORING SYSTEM</h3>
        <p style={{ margin: 0, fontSize: "11px", color: "#666" }}>
          Tailor Assignment Receipt
        </p>
      </div>

      {/* Tailor Details */}
      <div style={{ padding: "5px", borderBottom: "1px solid #000" }}>
        <div style={{ background: "#f5f5f5", padding: "6px", marginBottom: "8px" }}>
          <strong style={{ color: "#1f6b52" }}>👨‍🔧 TAILOR DETAILS</strong>
        </div>
        <p style={{ margin: "4px 0" }}><b>Name:</b> {tailor?.name || 'N/A'}</p>
        <p style={{ margin: "4px 0" }}><b>Phone:</b> {tailor?.phone || 'N/A'}</p>
        {tailor?.address && (
          <p style={{ margin: "4px 0" }}><b>Address:</b> {tailor.address}</p>
        )}
      </div>

      {/* Order Details */}
      <div style={{ padding: "5px", borderBottom: "1px solid #000" }}>
        <div style={{ background: "#f5f5f5", padding: "6px", marginBottom: "8px" }}>
          <strong style={{ color: "#1f6b52" }}>📋 ORDER DETAILS</strong>
        </div>
        <p style={{ margin: "4px 0" }}><b>Order No:</b> {order?.orderNumber || 'N/A'}</p>
        <p style={{ margin: "4px 0" }}><b>Garment Type:</b> {orderItem?.garmentType || 'N/A'}</p>
        <p style={{ margin: "4px 0" }}><b>Assigned Date:</b> {assignedAt ? new Date(assignedAt).toLocaleDateString('en-IN') : 'N/A'}</p>
        <p style={{ margin: "4px 0" }}><b>Due Date:</b> {order?.dueDate ? new Date(order.dueDate).toLocaleDateString('en-IN') : 'N/A'}</p>
      </div>

      {/* Cloth/Fabric Details */}
      <div style={{ padding: "5px", borderBottom: "1px solid #000" }}>
        <div style={{ background: "#f5f5f5", padding: "6px", marginBottom: "8px" }}>
          <strong style={{ color: "#1f6b52" }}>🧵 CLOTH/FABRIC DETAILS</strong>
        </div>
        <p style={{ margin: "4px 0" }}><b>Lot Number:</b> {lot?.lotNumber || 'N/A'}</p>
        <p style={{ margin: "4px 0" }}><b>Cloth Type:</b> {inventory?.clothType?.replace(/_/g, ' ') || 'N/A'}</p>
        <p style={{ margin: "4px 0" }}><b>Quantity Used:</b> {orderItem?.clothQuantityUsed || '0'} {lot?.unit || 'm'}</p>
        <p style={{ margin: "4px 0" }}><b>Color:</b> {lot?.color || 'N/A'}</p>
        {inventory?.pricePerUnit && (
          <p style={{ margin: "4px 0" }}><b>Rate:</b> ₹{Number(inventory.pricePerUnit).toLocaleString('en-IN')}/-</p>
        )}
      </div>

      {/* Customer Measurements - Only show measurements for the ordered garment type */}
      <div style={{ padding: "5px", borderBottom: "1px solid #000" }}>
        <div style={{ background: "#f5f5f5", padding: "6px", marginBottom: "8px" }}>
          <strong style={{ color: "#1f6b52" }}>📏 {orderItem?.garmentType || 'Garment'} Measurements</strong>
        </div>
        {customer?.measurements ? (
          <div style={{ fontSize: "11px" }}>
            {(() => {
              const garmentType = orderItem?.garmentType?.toLowerCase();
              const measurements = customer.measurements[garmentType];

              if (!measurements) {
                return <p style={{ margin: "4px 0", fontStyle: "italic", color: "#666" }}>No measurements available for {orderItem?.garmentType}</p>;
              }

              // Define measurement groups for each garment type (same as ViewAssignment)
              const garmentGroups = {
                shirt: {
                  length: ['length'],
                  shoulder: ['shoulder'],
                  chest: ['upper_chest', 'middle_chest', 'lower_chest'],
                  sleeve: ['sleeve_length', 'sleeve_elbow', 'sleeve_kaf'],
                  bicep: ['sleeve_bicep'],
                },
                pant: {
                  length: ['length'],
                  waist: ['waist'],
                  thigh: ['thigh'],
                  leg_opening: ['leg_opening'],

                },

              };

              const config = garmentGroups[garmentType] || garmentGroups['shirt'];

              return Object.entries(config).map(([group, fields]) => (
                <div key={group} style={{ marginBottom: "6px", display: "flex", alignItems: "center", gap: "8px" }}>
                  <strong style={{ minWidth: "70px", color: "#333", textTransform: "capitalize" }}>
                    {group}:
                  </strong>
                  <span style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                    {fields.map((field) => (
                      <span key={field} style={{ background: "#f5f5f5", padding: "2px 6px", borderRadius: "3px", fontSize: "10px" }}>
                        {measurements[field] || '-'}
                      </span>
                    ))}
                  </span>
                </div>
              ));
            })()}
          </div>
        ) : (
          <p style={{ margin: "4px 0", fontStyle: "italic", color: "#666" }}>No measurements available</p>
        )}
      </div>

      {/* Cost Details */}
      <div style={{ padding: "5px", borderBottom: "1px solid #000" }}>
        <div style={{ background: "#f5f5f5", padding: "6px", marginBottom: "8px" }}>
          <strong style={{ color: "#1f6b52" }}>💰 COST DETAILS</strong>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", margin: "4px 0" }}>
          <span><b>Labor Cost:</b></span>
          <span>₹{Number(laborCost || 0).toLocaleString('en-IN')}/-</span>
        </div>
        {orderItem?.sellingPrice && !isAdmin && (
          <div style={{ display: "flex", justifyContent: "space-between", margin: "4px 0" }}>
            <span><b>Selling Price:</b></span>
            <span>₹{Number(orderItem.sellingPrice).toLocaleString('en-IN')}/-</span>
          </div>
        )}
        {orderItem?.totalCost && !isAdmin && (
          <div style={{ display: "flex", justifyContent: "space-between", margin: "4px 0" }}>
            <span><b>Item Cost:</b></span>
            <span>₹{Number(orderItem.totalCost).toLocaleString('en-IN')}/-</span>
          </div>
        )}
      </div>

      {/* Notes */}
      {notes && (
        <div style={{ padding: "5px", borderBottom: "1px solid #000" }}>
          <div style={{ background: "#f5f5f5", padding: "6px", marginBottom: "8px" }}>
            <strong style={{ color: "#1f6b52" }}>📝 NOTES</strong>
          </div>
          <p style={{ margin: "4px 0", fontSize: "11px" }}>{notes}</p>
        </div>
      )}

      {/* QR Code */}
      <div style={{ padding: "6px", textAlign: "center", borderBottom: "1px solid #000" }}>
        <div style={{ background: "#f5f5f5", padding: "4px", marginBottom: "8px" }}>
          <strong style={{ color: "#1f6b52" }}>📱 SCAN FOR TRACKING</strong>
        </div>
        <div style={{ background: "#fff", padding: "8px", display: "inline-block" }}>
          <QRCode
            value={qrData}
            size={100}
            level={"M"}
            includeMargin={false}
          />
        </div>
        <p style={{ margin: "2px 0 0 0", fontSize: "10px", color: "#666" }}>
          Assignment ID: {assignment.id}
        </p>
      </div>

      {/* Footer */}
      {/* <div
        style={{
          background: "#1f6b52",
          color: "#fff",
          textAlign: "center",
          padding: "8px",
          fontSize: "11px"
        }}
      >
        <p style={{ margin: 0, color:"white"}}>Thank you for choosing our tailoring service</p>
        <p style={{ margin: "4px 0 0 0",color:"white", fontSize: "10px" }}>For queries contact: +91-XXX-XXX-XXXX</p>
      </div> */}
    </div>
  );
});

export default ReceiptTailor;
